import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { useState, type ReactElement, type SyntheticEvent } from "react";
import { MdaiLink } from "../MdaiLink";

export interface TabContentProps {
  alt?: string;
  content?: string | ReactElement;
  title: string;
  type?: "image" | "video" | "component";
  src?: string;
}

interface TabPanelProps {
  contentList: TabContentProps[];
  id: string;
  selectedValue: string;
  contentSize: {
    height: { sm: string; lg: string };
    width: { sm: string; lg: string };
  };
}

export interface TabsSectionProps {
  id: string;
  headline?: string | ReactElement;
  subhead?: string;
  contentList: TabContentProps[];
  contentSize: {
    height: {
      sm: string;
      md: string;
      lg: string;
    };
    width: {
      sm: string;
      md: string;
      lg: string;
    };
  };
  isPanelCard?: boolean;
  details?: string | ReactElement;
  caption?: string | ReactElement;
  button?: boolean;
}

const tabTitles = (contentList: TabContentProps[], id: string) =>
  contentList.map((item, idx) => (
    <Tab
      key={`${id}-tab-${item.title}`}
      label={item.title}
      value={String(idx + 1)}
    />
  ));

const tabPanels = ({ contentList, id, selectedValue, contentSize }: TabPanelProps) =>
  contentList.map((item: TabContentProps, idx: number) => {
    const panelValue = String(idx + 1);
    const isSelected = selectedValue === panelValue;

    switch (item.type) {
      case "image":
        return (
          <TabPanel key={`${id}-panel-${item.title}`} value={panelValue}>
            <Box
              component="img"
              sx={{
                height: "100%",
                width: "100%",
                order: 0,
                maxHeight: contentSize.height,
                maxWidth: contentSize.width,
                borderRadius: 2,
              }}
              alt={item.alt}
              loading={isSelected ? undefined : "lazy"}
              src={isSelected ? item.src : undefined}
            />
          </TabPanel>
        );
      case "video":
        return (
          <TabPanel key={`${id}-panel-${item.title}`} value={panelValue}>
            <Box
              component="div"
              sx={{
                display: "flex",
                justifyContent: "center",
                height: "100%",
                width: "100%",
                maxHeight: contentSize.height,
                maxWidth: contentSize.width,
                order: 0,
              }}
            >
              <video
                autoPlay={isSelected}
                loop
                muted
                preload={isSelected ? "metadata" : "none"}
                playsInline
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              >
                {isSelected && <source src={item.src} type="video/mp4" />}
                Sorry, your browser doesn't support embedded videos, but don't
                worry, you can
                <a href={item.src} download={item.src}>
                  download the MP4
                </a>
                and watch it with your favorite video player!
              </video>
            </Box>
          </TabPanel>
        );

      default:
        return (
          <TabPanel key={`${id}-panel-${item.title}`} value={panelValue}>
            <Box
              sx={{
                maxHeight: contentSize.height,
                maxWidth: contentSize.width,
                borderRadius: 4,
              }}
            >
              {item.content}
            </Box>
          </TabPanel>
        );
    }
  });

export function TabsSection({
  contentList,
  id,
  headline,
  subhead,
  contentSize,
  details,
  caption,
  button = true,
  isPanelCard = false,
}: TabsSectionProps) {
  const [value, setValue] = useState("1");

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyItems: "center",
        flexDirection: "column",
        alignItems: "center",
        typography: "body1",
        width: "100%",
      }}
    >
      <Stack gap={2} alignItems="center">
        <Typography
          variant={isPanelCard ? "h3" : "h2"}
          sx={{
            p: "0",
            mb: 1,
            textAlign: "center",
            maxWidth: "1000px",
          }}
        >
          {headline}
        </Typography>

        {subhead && (
          <Typography
            variant="h3"
            sx={{
              p: "0",
              mb: 5,
            }}
          >
            {subhead}
          </Typography>
        )}
        {typeof details === "string" ? (
          <Typography
            variant="body1"
            sx={{
              maxWidth: "600px",
              textAlign: "left",
            }}
          >
            {details}
          </Typography>
        ) : (
          details
        )}
      </Stack>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            aria-label="Toggle between SmartHub visuals"
            textColor="primary"
            onChange={handleChange}
          >
            {tabTitles(contentList, id)}
          </TabList>
        </Box>
        <Box sx={{ minHeight: contentSize.height }}>
          {isPanelCard ? (
            <Card
              key={id}
              sx={{
                border: "1px solid #D500F9",
                backgroundColor: "transparent",
                borderRadius: "8px",
                mb: 3,
              }}
            >
              <CardContent>
                  {tabPanels({ contentList, id, selectedValue: value, contentSize })}
              </CardContent>
            </Card>
          ) : (
            tabPanels({ contentList, id, selectedValue: value, contentSize })
          )}
        </Box>
      </TabContext>
      <Stack
        sx={{
          maxWidth: "600px",
          textAlign: { sm: "center", md: "left" },
        }}
      >
        {caption}
        {button && (
          <MdaiLink
            label="Get started with docs"
            href="https://docs.mydecisive.ai/"
            variant="outlined"
            style={{
              alignSelf: "center",
              border: "1px solid #fff",
              borderRadius: "4px",
              color: "#fff",
              maxWidth: "270px",
              padding: "8px 22px",
              textAlign: "center",
            }}
          />
        )}
      </Stack>
    </Box>
  );
}
